// Printify API service – handles basic requests to Printify
// Docs: https://developers.printify.com/#/

const PRINTIFY_API_KEY = import.meta.env.VITE_PRINTIFY_API_KEY; // Add this to your .env file

const BASE_URL = 'https://api.printify.com/v1';

if (!PRINTIFY_API_KEY && import.meta.env.DEV) {
  // eslint-disable-next-line no-console
  console.warn('VITE_PRINTIFY_API_KEY is not set Printify API requests will fail');
}

/**
 * Generic helper to call the Printify API (server-side).
 * @param {string} endpoint – e.g. "/shops.json"
 * @param {Object} options – fetch options (method, body, etc.)
 */
async function request(endpoint, { method = 'GET', body } = {}) {
  const url = `${BASE_URL}${endpoint}`;
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${PRINTIFY_API_KEY}`,
  };

  const res = await fetch(url, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Printify API error ${res.status}: ${errorText}`);
  }

  return res.json();
}

export async function getShops() {
  return request('/shops.json');
}

export async function getProducts(shopId, { page = 1, limit = 20, status } = {}) {
  const isBrowser = typeof window !== 'undefined';
  const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
  const qs = new URLSearchParams({ page, limit, ...(status ? { status } : {}) }).toString();

  if (isBrowser) {
    const url = `${API_BASE}/api/printify/products?${qs}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Proxy fetch failed');
    return res.json();
  }
  return request(`/shops/${shopId}/products.json?${qs}`);
}

export async function getProduct(shopId, productId) {
  const isBrowser = typeof window !== 'undefined';
  const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
  if (isBrowser) {
    const url = `${API_BASE}/api/printify/products/${productId}`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Proxy fetch failed');
    return res.json();
  }
  return request(`/shops/${shopId}/products/${productId}.json`);
}

export async function createOrder(shopId, orderData) {
  return request(`/shops/${shopId}/orders.json`, { method: 'POST', body: orderData });
}

export async function sendOrderToProduction(shopId, orderId) {
  return request(`/shops/${shopId}/orders/${orderId}/send_to_production.json`, { method: 'POST' });
}

export async function publishProduct(shopId, productId, options = {}) {
  const isBrowser = typeof window !== 'undefined';
  const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
  if (isBrowser) {
    const url = `${API_BASE}/api/printify/products/${productId}/publish`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(options),
    });
    if (!res.ok) throw new Error('Publish failed');
    return res.json();
  }
  return request(`/shops/${shopId}/products/${productId}/publish.json`, { method: 'POST', body: options });
}

// ---- Custom mock-up helpers (proxy routes) ----
export async function generateMockup(productId, variantIds = [], imageUrl) {
  const isBrowser = typeof window !== 'undefined';
  const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
  const url = `${API_BASE}/api/printify/mockups/generate`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ productId, variantIds, imageUrl }),
  });
  if (!res.ok) throw new Error('Mock-up generation failed');
  return res.json(); // returns {mockup_task_id}
}

export async function getMockupStatus(taskId) {
  const API_BASE = import.meta.env.VITE_API_BASE_URL || '';
  const res = await fetch(`${API_BASE}/api/printify/mockups/${taskId}`);
  if (!res.ok) throw new Error('Failed fetching mock-up status');
  return res.json();
}

export default {
  getShops,
  getProducts,
  getProduct,
  createOrder,
  sendOrderToProduction,
  publishProduct,
  generateMockup,
  getMockupStatus,
}; 